import React from "react";
import "./DeadlineField.css";

// Values are datetime-local strings ('YYYY-MM-DDTHH:mm', local time).
const pad = (n) => String(n).padStart(2, "0");

const toLocalInputValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
  `T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const parseLocalInputValue = (value) => {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date) ? date : null;
};

// The datetime-local value moved on by `days` days or `months` calendar months
// (clamped to the end of a shorter month, e.g. 31 Jan + 1m = 28/29 Feb). An
// empty/invalid value counts from now.
export const shiftLocalDatetime = (value, { days = 0, months = 0 }) => {
  const date = parseLocalInputValue(value) || new Date();
  if (months) {
    const day = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + months);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(day, lastDay));
  }
  if (days) date.setDate(date.getDate() + days);
  return toLocalInputValue(date);
};

const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;

// How far the deadline is from today, by calendar date: 'Today', 'Tomorrow',
// 'N days' (under a week), 'N weeks' (under a month), 'N months', 'N years';
// past deadlines read 'Yesterday' / '... ago'. '' for an empty/invalid value.
export const describeDeadline = (value, now = new Date()) => {
  const date = parseLocalInputValue(value);
  if (!date) return "";
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfDay(date) - startOfDay(now)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";

  const [from, to] = days > 0 ? [now, date] : [date, now];
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  const absDays = Math.abs(days);

  let text;
  if (absDays < 7) text = plural(absDays, "day");
  else if (months < 1) text = plural(Math.floor(absDays / 7), "week");
  else if (months < 12) text = plural(months, "month");
  else text = plural(Math.floor(months / 12), "year");
  return days > 0 ? text : `${text} ago`;
};

// A task's deadline input with +1d / +7d / +1m buttons and how far away it is.
// Props:
//   name     – passed back in onChange's e.target.name
//   value    – datetime-local string
//   onChange – (event-like { target: { name, value } }) => void, as for an input
const DeadlineField = ({ name, value, onChange }) => {
  const change = (next) => onChange({ target: { name, value: next } });
  const relative = describeDeadline(value);
  return (
    <div className="deadline-field">
      <input
        autoComplete="off"
        type="datetime-local"
        name={name}
        value={value}
        onChange={(e) => change(e.target.value)}
        required
      />
      <button type="button" onClick={() => change(shiftLocalDatetime(value, { days: 1 }))}>
        +1d
      </button>
      <button type="button" onClick={() => change(shiftLocalDatetime(value, { days: 7 }))}>
        +7d
      </button>
      <button type="button" onClick={() => change(shiftLocalDatetime(value, { months: 1 }))}>
        +1m
      </button>
      {relative && (
        <span
          className={`deadline-relative${relative.endsWith("ago") || relative === "Yesterday" ? " is-past" : ""}`}
        >
          {relative}
        </span>
      )}
    </div>
  );
};

export default DeadlineField;
